import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Paper,
  Switch,
  FormControlLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  GpsFixed as GpsIcon,
  LocalShipping as TruckIcon,
  Person as PersonIcon,
  Speed as SpeedIcon,
  Schedule as TimeIcon,
  Refresh as RefreshIcon,
  PlayArrow as InProgressIcon,
  CheckCircle as CompleteIcon,
  Pause as IdleIcon,
  Warning as WarningIcon,
  MyLocation as LocationIcon,
  Map as MapIcon,
  ExpandMore as ExpandMoreIcon,
  MonitorHeart as MonitorIcon,
  FactCheck as InspectionIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import MapboxMiniMap from '../Maps/MapboxMiniMap';
import MapboxFleetMap from '../Maps/MapboxFleetMap';
import TruckDetailsModal from '../Maps/TruckDetailsModal';
import TruckJobSummary, { truckJobHeadingKey } from '../Maps/TruckJobSummary';
import RouteProgressChip, { ROUTE_PROGRESS_REFRESH_MS } from '../RoutePlanning/RouteProgressChip';
import { reverseGeocodeShortCached } from '../../utils/geocoding';

// TKT-235: localStorage key for the dispatcher's Show All Trucks choice
// so the mode survives page refreshes.
const SHOW_ALL_TRUCKS_KEY = 'liveTracking.showAllTrucks';

const LiveTracking = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [truckLocations, setTruckLocations] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // TKT-235 (Doug): default stays active-only; "Show All Trucks" expands
  // the list to parked / offline / out-of-service vehicles.
  const [showAllTrucks, setShowAllTrucks] = useState(
    () => localStorage.getItem(SHOW_ALL_TRUCKS_KEY) === 'true'
  );
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'map'
  const [truckAddresses, setTruckAddresses] = useState({});
  // TKT-236: admin-only ingest diagnostics. The endpoint is requireAdmin, so
  // a 403 simply leaves diagnostics null and the panel stays hidden.
  const [diagnostics, setDiagnostics] = useState(null);
  // TKT-420: live ahead/behind + predicted finish per truck (office roles).
  const [routeProgress, setRouteProgress] = useState({});
  const containerRef = useRef(null);

  // ====================================
  // DATA FETCHING
  // ====================================

  const fetchLiveLocations = async (preserveScroll = false) => {
    // Save current scroll position if preserving
    const scrollPos = preserveScroll ? window.scrollY : 0;
    
    try {
      // Only show loading spinner on initial load, not on auto-refresh
      if (!preserveScroll) {
        setLoading(true);
      }
      const response = await makeApiCall(
        `api/gps/locations/current${showAllTrucks ? '?include_inactive=true' : ''}`
      );
      
      if (response.ok) {
        const data = await response.json();
        const locations = data.truck_locations || [];
        setTruckLocations(locations);
        setLastUpdated(new Date(data.current_time));
        
        // Fetch addresses for trucks with GPS data
        fetchAddressesForTrucks(locations);
        
        // Restore scroll position after a brief delay to let DOM update
        if (preserveScroll && scrollPos > 0) {
          setTimeout(() => {
            window.scrollTo({ top: scrollPos, behavior: 'auto' });
          }, 100);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load GPS data');
      }
    } catch (error) {
      console.error('Error fetching GPS locations:', error);
      setError('Failed to load GPS tracking data');
    } finally {
      setLoading(false);
    }
  };

  // TKT-236: pull ingest diagnostics (admin only). Silently no-ops for
  // non-admins (403) so the panel never renders for crew users.
  const fetchDiagnostics = async () => {
    try {
      const response = await makeApiCall('api/gps/diagnostics');
      if (response.ok) {
        setDiagnostics(await response.json());
      } else {
        setDiagnostics(null);
      }
    } catch (err) {
      setDiagnostics(null);
    }
  };

  // Fetch addresses for trucks with GPS coordinates
  const fetchAddressesForTrucks = async (trucks) => {
    const addressPromises = trucks
      .filter(truck => truck.latitude && truck.longitude)
      .map(async (truck) => {
        try {
          const address = await reverseGeocodeShortCached(truck.latitude, truck.longitude);
          return { truckId: truck.truck_id, address };
        } catch (error) {
          console.warn(`Failed to geocode address for truck ${truck.truck_name}:`, error);
          return { truckId: truck.truck_id, address: `${truck.latitude.toFixed(4)}, ${truck.longitude.toFixed(4)}` };
        }
      });

    try {
      const addressResults = await Promise.all(addressPromises);
      const addressMap = {};
      addressResults.forEach(({ truckId, address }) => {
        addressMap[truckId] = address;
      });
      setTruckAddresses(addressMap);
    } catch (error) {
      console.warn('Error fetching truck addresses:', error);
    }
  };

  // ====================================
  // AUTO REFRESH
  // ====================================

  // Initial load + refetch whenever the Show All Trucks mode changes.
  useEffect(() => {
    fetchLiveLocations();
    fetchDiagnostics();
  }, [showAllTrucks]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchLiveLocations(true); // Preserve scroll position on auto-refresh
        fetchDiagnostics();
      }, 30000); // Refresh every 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, showAllTrucks]);

  useEffect(() => {
    let cancelled = false;
    const loadProgress = () => {
      makeApiCall('api/routes/live-progress')
        .then((response) => (response.ok ? response.json() : { trucks: [] }))
        .then((payload) => {
          if (cancelled) return;
          const byTruck = {};
          (payload.trucks || []).forEach((truck) => {
            byTruck[truck.truck_id] = truck;
          });
          setRouteProgress(byTruck);
        })
        .catch(() => {});
    };
    loadProgress();
    const id = autoRefresh ? setInterval(loadProgress, ROUTE_PROGRESS_REFRESH_MS) : null;
    return () => {
      cancelled = true;
      if (id) clearInterval(id);
    };
  }, [autoRefresh]);

  const handleShowAllTrucksChange = (checked) => {
    localStorage.setItem(SHOW_ALL_TRUCKS_KEY, checked ? 'true' : 'false');
    setShowAllTrucks(checked);
  };

  // ====================================
  // UTILITY FUNCTIONS
  // ====================================

  // TKT-189: stale GPS reads are the same as offline from a dispatcher's
  // perspective. If the last ping is more than STALE_AFTER_MIN minutes
  // old, treat the truck as Offline regardless of ignition value, and
  // surface a "Stale" warning on the card so the user knows the data is
  // not live.
  const STALE_AFTER_MIN = 15;

  const isLastUpdateStale = (truck) => {
    if (!truck?.last_update) return true;
    const diffMs = Date.now() - new Date(truck.last_update).getTime();
    return diffMs > STALE_AFTER_MIN * 60 * 1000;
  };

  const getTruckStatus = (truck) => {
    // TKT-235: in Show All Trucks mode, out-of-service vehicles are
    // labeled explicitly so dispatchers can tell them apart from trucks
    // that should be reporting but aren't.
    if (truck.is_active === false) {
      return { status: 'out_of_service', color: 'error', text: t('liveTracking.outOfService', 'Out of Service') };
    }
    if (!truck.latitude || !truck.longitude) {
      return { status: 'offline', color: 'default', text: 'No GPS data' };
    }
    if (isLastUpdateStale(truck)) {
      return { status: 'offline', color: 'warning', text: 'Offline (stale)' };
    }
    if (!truck.ignition_status) {
      return { status: 'offline', color: 'default', text: 'Offline' };
    }

    if (truck.ignition_status === 'On') {
      if (truck.current_speed > 5) {
        return { status: 'driving', color: 'info', text: 'Driving' };
      } else if (truck.job_in_progress) {
        return { status: 'working', color: 'warning', text: 'On Site' };
      } else {
        return { status: 'idle', color: 'default', text: 'Idle' };
      }
    }

    return { status: 'parked', color: 'success', text: 'Parked' };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'driving': return <GpsIcon />;
      case 'working': return <InProgressIcon />;
      case 'idle': return <IdleIcon />;
      case 'parked': return <CompleteIcon />;
      default: return <WarningIcon />;
    }
  };

  const formatLastUpdate = (lastUpdate) => {
    if (!lastUpdate) return 'No data';
    const diff = new Date() - new Date(lastUpdate);
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ago`;
  };

  const openLocationInMaps = (latitude, longitude, name) => {
    const url = `https://www.google.com/maps?q=${latitude},${longitude}&z=15`;
    window.open(url, '_blank');
  };

  const handleTruckClick = (truck) => {
    setSelectedTruck(truck);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTruck(null);
  };

  // ====================================
  // RENDER FUNCTIONS
  // ====================================

  const renderTrackingControls = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">
              Fleet Tracking Dashboard
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {lastUpdated && `Last updated: ${lastUpdated.toLocaleString()}`}
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant={viewMode === 'cards' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setViewMode('cards')}
            >
              Cards
            </Button>
            <Button
              variant={viewMode === 'map' ? 'contained' : 'outlined'}
              size="small"
              startIcon={<MapIcon />}
              onClick={() => setViewMode('map')}
            >
              Map View
            </Button>
            
            <Tooltip title={t('liveTracking.showAllTrucksTooltip', 'Include parked, offline and out-of-service trucks')}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAllTrucks}
                    onChange={(e) => handleShowAllTrucksChange(e.target.checked)}
                    inputProps={{ 'aria-label': t('liveTracking.showAllTrucks', 'Show All Trucks') }}
                  />
                }
                label={t('liveTracking.showAllTrucks', 'Show All Trucks')}
              />
            </Tooltip>

            <Tooltip title="Updates truck positions every 30 seconds without disrupting map view">
              <FormControlLabel
                control={
                  <Switch
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                  />
                }
                label="Auto Refresh (30s)"
              />
            </Tooltip>
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchLiveLocations(true)}
              disabled={loading}
            >
              Refresh Now
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Today's completed vehicle inspection (circle check) for the truck.
  const renderInspectionChip = (truck) => {
    if (!truck.inspection_id) {
      return (
        <Chip
          icon={<InspectionIcon />}
          label={t('liveTracking.inspectionNotDone', 'No inspection today')}
          color="warning"
          size="small"
          variant="outlined"
        />
      );
    }
    const time = new Date(truck.inspection_completed_at).toLocaleTimeString(i18n.language, {
      hour: 'numeric',
      minute: '2-digit',
    });
    let color = 'success';
    let label = t('liveTracking.inspectionDone', 'Inspected {{time}}', { time });
    if (truck.inspection_has_major_defect) {
      color = 'error';
      label = t('liveTracking.inspectionMajorDefect', 'Inspected {{time}} · major defect', { time });
    } else if (truck.inspection_has_minor_defect) {
      color = 'warning';
      label = t('liveTracking.inspectionMinorDefect', 'Inspected {{time}} · minor defect', { time });
    }
    const chip = <Chip icon={<InspectionIcon />} label={label} color={color} size="small" />;
    return truck.inspection_inspector_name ? (
      <Tooltip title={t('liveTracking.inspectedBy', 'Inspected by {{name}}', { name: truck.inspection_inspector_name })}>
        {chip}
      </Tooltip>
    ) : chip;
  };

  const renderTruckCard = (truck) => {
    const status = getTruckStatus(truck);
    
    return (
      <Card key={truck.truck_id} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box display="flex" alignItems="center">
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <TruckIcon />
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {truck.truck_name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {truck.fence_vehicle_name && `GPS: ${truck.fence_vehicle_name}`}
                </Typography>
              </Box>
            </Box>
            
            <Box display="flex" flex-direction="column" alignItems="flex-end" gap={1}>
              <Chip
                icon={getStatusIcon(status.status)}
                label={status.text}
                color={status.color}
                size="small"
              />
              {truck.current_speed !== null && (
                <Chip
                  icon={<SpeedIcon />}
                  label={`${Math.round(truck.current_speed)} km/h`}
                  size="small"
                  variant="outlined"
                />
              )}
              {renderInspectionChip(truck)}
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom>
                Driver Information
              </Typography>
              {truck.driver_username ? (
                <Box display="flex" alignItems="center">
                  <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    {truck.driver_first_name} {truck.driver_last_name}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No driver assigned
                </Typography>
              )}
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t(truckJobHeadingKey(truck))}
              </Typography>
              <TruckJobSummary truck={truck} />
              {routeProgress[truck.truck_id] && (
                <Box mt={1}>
                  <RouteProgressChip progress={routeProgress[truck.truck_id]} />
                </Box>
              )}
            </Grid>
          </Grid>

          {truck.latitude && truck.longitude ? (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Location & Status
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ 
                    minHeight: '120px', 
                    width: '100%',
                    position: 'relative',
                    '@media (max-width: 600px)': {
                      minHeight: '180px'
                    }
                  }}>
                    <MapboxMiniMap
                      latitude={truck.latitude}
                      longitude={truck.longitude}
                      status={status.status}
                      speed={truck.speed_kph}
                      height="120px"
                      truckName={truck.truck_name}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="body2">
                      {truckAddresses[truck.truck_id] || 'Loading address...'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatLastUpdate(truck.last_update)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                      {truck.latitude.toFixed(4)}, {truck.longitude.toFixed(4)}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LocationIcon />}
                      onClick={() => handleTruckClick(truck)}
                      sx={{ alignSelf: 'flex-start', mt: 1 }}
                    >
                      View Details
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              
              {truck.fuel_level_percentage !== null && (
                <Box mt={2}>
                  <Typography variant="caption" color="textSecondary">
                    Fuel: {Math.round(truck.fuel_level_percentage)}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={truck.fuel_level_percentage} 
                    sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                    color={truck.fuel_level_percentage > 25 ? 'primary' : 'error'}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box
              mt={2}
              p={2}
              sx={{
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.default',
              }}
              role="status"
              aria-label={`No GPS data available for ${truck.truck_name || 'this truck'}`}
            >
              <Typography variant="body2" color="textSecondary">
                <WarningIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                No GPS data has been received for this truck.
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
                Confirm the GPS unit is powered and the vehicle name matches the integration profile.
              </Typography>
            </Box>
          )}

          {truck.latitude && truck.longitude && isLastUpdateStale(truck) && (
            <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon fontSize="small" />}>
              GPS data is more than {STALE_AFTER_MIN} minutes old. The position
              shown above may not reflect where the truck is right now.
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  // TKT-236: lightweight ingest health panel so dispatchers/admins can tell at
  // a glance whether the GPS feed is actually reaching the database, rather
  // than guessing when a truck reads "Offline (stale)".
  const renderDiagnostics = () => {
    if (!diagnostics) return null;
    const o = diagnostics.overall || {};
    const trucks = diagnostics.trucks || [];
    const unmatched = diagnostics.unmatched_devices || [];
    const svc = diagnostics.service || {};
    const staleAfter = diagnostics.stale_after_min || 15;
    const ageMin = o.last_event_age_min;
    const ageColor = ageMin == null
      ? 'text.secondary'
      : ageMin <= staleAfter
        ? 'success.main'
        : ageMin <= 30
          ? 'warning.main'
          : 'error.main';
    const staleCount = trucks.filter((tr) => tr.is_stale).length;
    const ageLabel = ageMin == null
      ? t('liveTracking.diagnostics.noData', 'No data')
      : t('liveTracking.diagnostics.minutesAgo', '{{count}} min ago', { count: ageMin });

    return (
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-label={t('liveTracking.diagnostics.expand', 'Expand GPS ingest diagnostics')}
        >
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <MonitorIcon color="action" />
            <Typography variant="subtitle1">
              {t('liveTracking.diagnostics.title', 'GPS ingest diagnostics')}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={`${t('liveTracking.diagnostics.lastIngest', 'Last ingest')}: ${ageLabel}`}
              sx={{ color: ageColor, fontWeight: 600, borderColor: ageColor }}
            />
            {staleCount > 0 && (
              <Chip
                size="small"
                color="warning"
                label={t('liveTracking.diagnostics.staleCount', '{{count}} stale', { count: staleCount })}
              />
            )}
            {unmatched.length > 0 && (
              <Chip
                size="small"
                color="error"
                label={t('liveTracking.diagnostics.unmatchedCount', '{{count}} unmatched', { count: unmatched.length })}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {t('liveTracking.diagnostics.poller', 'Poller')}:{' '}
            {svc.isRunning
              ? t('liveTracking.diagnostics.running', 'running')
              : t('liveTracking.diagnostics.stopped', 'stopped')}
            {svc.cronExpression ? ` (${svc.cronExpression})` : ''}
            {' \u2022 '}
            {t('liveTracking.diagnostics.rowsLastHour', '{{count}} pings in last hour', { count: o.rows_last_hour || 0 })}
          </Typography>

          {unmatched.length > 0 && (
            <Alert severity="warning" sx={{ my: 1 }} icon={<WarningIcon fontSize="small" />}>
              {t('liveTracking.diagnostics.unmatchedHint', 'These devices are sending GPS but are not matched to a truck (check fence_vehicle_name):')}{' '}
              {unmatched.map((u) => u.device_name).join(', ')}
            </Alert>
          )}

          <Table size="small" aria-label={t('liveTracking.diagnostics.tableLabel', 'Per-truck GPS freshness')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('liveTracking.diagnostics.truck', 'Truck')}</TableCell>
                <TableCell>{t('liveTracking.diagnostics.lastPing', 'Last ping')}</TableCell>
                <TableCell>{t('liveTracking.diagnostics.status', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trucks.map((tr) => (
                <TableRow key={tr.truck_id}>
                  <TableCell>{tr.truck_name}</TableCell>
                  <TableCell>
                    {tr.last_event_age_min == null
                      ? t('liveTracking.diagnostics.noData', 'No data')
                      : t('liveTracking.diagnostics.minutesAgo', '{{count}} min ago', { count: tr.last_event_age_min })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={tr.is_stale ? 'default' : 'success'}
                      label={tr.is_stale
                        ? t('liveTracking.diagnostics.stale', 'Stale')
                        : t('liveTracking.diagnostics.fresh', 'Fresh')}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
    );
  };

const renderTrackingSummary = () => {
    // TKT-189: only count a truck as "active" when its GPS data is
    // fresh AND the ignition is on. Counting stale rows as active
    // hid the fact that several trucks were not reporting.
    const activeTrucks = truckLocations.filter(
      (truck) => truck.ignition_status === 'On' && !isLastUpdateStale(truck)
    ).length;
    const trucksWithJobs = truckLocations.filter(truck => truck.job_in_progress).length;
    const totalJobs = truckLocations.reduce((sum, truck) => sum + (truck.total_stops || 0), 0);
    const completedJobs = truckLocations.reduce((sum, truck) => sum + (truck.jobs_completed || 0), 0);
    const inspectedTrucks = truckLocations.filter((truck) => truck.inspection_id).length;

    return (
      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 6, md: 2.4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            {/* TKT-235: Active / Total so the dispatcher can confirm the
                whole fleet is accounted for in the chosen mode. */}
            <Typography variant="h4" color="primary.main">
              {activeTrucks} / {truckLocations.length}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('liveTracking.activeOfTotal', 'Active / Total Trucks')}
            </Typography>
          </Paper>
        </Grid>
        
        <Grid size={{ xs: 6, md: 2.4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">
              {trucksWithJobs}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              On Jobs
            </Typography>
          </Paper>
        </Grid>
        
        <Grid size={{ xs: 6, md: 2.4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="info.main">
              {totalJobs}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Jobs
            </Typography>
          </Paper>
        </Grid>
        
        <Grid size={{ xs: 6, md: 2.4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {completedJobs}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Completed
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 6, md: 2.4 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography
              variant="h4"
              color={inspectedTrucks === truckLocations.length ? 'success.main' : 'warning.main'}
            >
              {inspectedTrucks} / {truckLocations.length}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('liveTracking.inspectedToday', 'Inspected Today')}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // ====================================
  // MAIN RENDER
  // ====================================

  return (
    <Box ref={containerRef} sx={{ pb: 4 }}>
      <Typography variant="h5" gutterBottom>
        Live Fleet Tracking
      </Typography>
      
      {renderTrackingControls()}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box>
          <LinearProgress />
          <Typography sx={{ mt: 2, textAlign: 'center' }}>
            Loading GPS data...
          </Typography>
        </Box>
      ) : (
        <Box>
          {renderTrackingSummary()}

          {renderDiagnostics()}

          {truckLocations.length > 0 ? (
            viewMode === 'cards' ? (
              <Box sx={{ mt: 2 }}>
                {truckLocations.map(truck => renderTruckCard(truck))}
              </Box>
            ) : (
              <Box sx={{ 
                height: { xs: '500px', md: '700px' }, 
                width: '100%',
                minHeight: '400px',
                position: 'relative'
              }}>
                <MapboxFleetMap
                  trucks={truckLocations}
                  height="100%"
                  onTruckClick={handleTruckClick}
                />
              </Box>
            )
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <GpsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No GPS Data Available
              </Typography>
              <Typography variant="body2" color="textSecondary">
                GPS tracking data is not available. Check GPS device connections.
              </Typography>
            </Paper>
          )}
        </Box>
      )}
      
      {/* Truck Details Modal */}
      <TruckDetailsModal
        open={showModal}
        onClose={handleCloseModal}
        truck={selectedTruck}
        allTrucks={truckLocations}
      />
    </Box>
  );
};

export default LiveTracking;
